// src/pages/PayNow.tsx
import { useEffect, useState } from "react";
import { authFetch } from "../lib/auth";

type MyPay = {
  id: number;
  order_id: string;
  title: string | null;
  amount: number;
  status: string;
  pay_url?: string | null;
  created_at: string;
};

export default function PayNow() {
  const [rows, setRows] = useState<MyPay[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string>("");
  const [busyId, setBusyId] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      // 1차: 신규 경로
      let r = await authFetch("/api/payments/my/approved");
      if (!r.ok) {
        // 2차: 호환 경로
        r = await authFetch("/api/payments/my?status=approved");
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(j?.message || `조회 실패 (${r.status})`);
      }
      setRows(j?.items || j?.payments || j?.rows || []);
    } catch (e: any) {
      setErr(e?.message || "승인된 결제 조회에 실패했습니다.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const fmtAmount = (n: number) => n.toLocaleString("ko-KR");
  const fmtDT = (s: string) => {
    const d = new Date(s);
    return isNaN(+d) ? s : d.toLocaleString("ko-KR");
  };

  // ✅ EzPg 결제창 진입: pay-form 페이지를 새 창으로 열기
  async function startPay(id: number) {
    try {
      setBusyId(id);

      const token = localStorage.getItem("token");
      if (!token) {
        alert("로그인이 필요합니다.");
        return;
      }

      const url = `/api/payments/${id}/pay-form?token=${encodeURIComponent(
        token
      )}`;

      const popup = window.open(
        url,
        "EZPG_PAY_WINDOW",
        "width=430,height=700,scrollbars=yes"
      );

      if (!popup) {
        alert(
          "브라우저에서 팝업이 차단되었습니다.\n" +
            "주소창 오른쪽의 팝업 차단 아이콘을 눌러 팝업을 허용한 뒤 다시 시도해주세요."
        );
      }
    } catch (e: any) {
      alert(e?.message || "결제 준비 실패");
    } finally {
      setTimeout(() => setBusyId(null), 800);
    }
  }

  return (
    <main className="max-w-3xl mx-auto p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">승인된 결제</h1>
        <button className="btn btn-sm" onClick={load} disabled={loading}>
          새로고침
        </button>
      </div>

      {err && (
        <div className="mb-3 rounded bg-red-100 text-red-700 px-3 py-2 text-sm">
          {err}
        </div>
      )}

      {loading && <div className="text-gray-500">불러오는 중…</div>}

      {!loading && rows.length === 0 && !err && (
        <div className="text-gray-500">승인된 결제가 없습니다.</div>
      )}

      {rows.map((p) => (
        <div key={p.id} className="border rounded p-4 mb-3 bg-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold">
                {p.title || `주문 ${p.order_id}`}
              </div>
              <div className="text-sm text-gray-600">주문ID: {p.order_id}</div>
              <div className="text-sm text-gray-600">
                생성일: {fmtDT(p.created_at)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold">{fmtAmount(p.amount)} 원</div>
              <div className="text-xs text-gray-500 mt-1">{p.status}</div>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <button
              className="btn btn-primary"
              onClick={() => startPay(p.id)}
              disabled={busyId === p.id}
            >
              {busyId === p.id ? "진행 중…" : "결제하기"}
            </button>

            {/* 이미 pay_url이 있는 경우 보조 옵션으로 유지 */}
            {p.pay_url && (
              <a
                className="btn btn-outline"
                href={p.pay_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                링크로 열기
              </a>
            )}
          </div>
        </div>
      ))}
    </main>
  );
}
